import { Box, Flex, rem, SimpleGrid, Text, Image, useComputedColorScheme } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { getFDTLabelImageName } from "@utils/UIUtils";
import { useEffect, useState } from "react";
import { FDTSnapshot, FDTLabel, Fovea, TestType } from "types/testTypes";

interface FDTGrid18Props {
  eye: string;
  degree: string;
  withBorder?: boolean;
  data?: FDTSnapshot[];
  fovea?: Fovea;
  testType?: TestType;
  displayImage?: boolean;
}

export default function FDTGrid18(props: FDTGrid18Props) {
  const { eye, degree, withBorder, data, testType, displayImage, fovea } = props;
  const [dataWithPadding, setDataWithPadding] = useState(null);
  useEffect(() => {
    if (data && data.length > 0) {
      const paddedData = [...data];
      getExcludedBoxes(eye, degree).forEach((i) => {
        paddedData.splice(i, 0, null);
      });
      setDataWithPadding(paddedData);
    }
  }, [data, degree]);

  const is20 = degree === "20";
  const isDark = useComputedColorScheme("light") === "dark";

  const getExcludedBoxes = (eye: string, degree: string) => {
    if (is20) {
      return [];
    }
    if (eye === "leftEye") {
      if (degree === "30") {
        return [4, 19];
      }
    } else if (eye === "rightEye") {
      if (degree === "30") {
        return [0, 15];
      }
    }
  };

  const renderValue = (
    value: { label: FDTLabel; value: number },
    key?: string,
    isCenter?: boolean,
  ) => {
    return testType ? (
      value?.label ? (
        <Image
          key={key}
          src={`img/fdt-img/${getFDTLabelImageName(testType, value.label)}.png`}
          fit="cover"
          h={"100%"}
          radius={isCenter && "50%"}
        />
      ) : (
        <IconX />
      )
    ) : (
      <Text h={"100%"} ta={"center"} fz={"md"}>
        {value.value}
      </Text>
    );
  };

  return (
    <Box w={"100%"} miw={350} pos="relative">
      <Box
        pos="absolute"
        style={{
          inset: 0,
          backgroundImage: "url(/img/test_backgrounds/fdt-n-30-bgs.png)",
          backgroundSize: "cover",
          filter: isDark ? "invert(1)" : "none",
        }}
      />
      <Box
        pos="relative"
        py={"8.3%"}
        ml={eye === "leftEye" || is20 ? "16.66%" : ""}
        mr={eye === "rightEye" || is20 ? "16.66%" : ""}
      >
        <Box
          pos="absolute"
          top="50%"
          left={is20 ? "50%" : eye === "leftEye" ? "40%" : "60%"}
          p={displayImage ? rem(2) : "md"}
          style={{
            transform: "translate(-50%, -50%)",
            width: is20 ? "25%" : "20%",
            aspectRatio: 1,
            border: withBorder && "2px solid var(--oculera-border)",
            backgroundColor: "var(--oculera-surface)",
            zIndex: 10,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {fovea?.value !== undefined &&
            renderValue(fovea as { label: FDTLabel; value: number }, "-1", true)}
        </Box>
        <SimpleGrid cols={is20 ? 4 : 5} spacing={rem(0)} verticalSpacing={rem(0)}>
          {dataWithPadding?.map((v, i) => (
            <Box
              p={displayImage ? rem(2) : "md"}
              key={i}
              style={
                !getExcludedBoxes(eye, degree).includes(i) && {
                  width: "100%",
                  height: "100%",
                  border: withBorder && "1px solid var(--oculera-border)",
                  aspectRatio: 1,
                  zIndex: 0,
                }
              }
            >
              <Flex justify={"center"} align={"center"} h={"100%"}>
                {!getExcludedBoxes(eye, degree).includes(i) && renderValue(v, i)}
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
}
